import { useMemo, useState } from 'react'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnConditionValue,
  ConditionValue,
  IActionRepeaterValue,
  thousandFmtNoDecimal,
  TypographyGroupValue,
} from '@makyoapp/makyo-common'
import {
  BaseComponent,
  Button,
  ColorVariant,
  ComponentAlignment,
  CornerRadius,
  getShapeClass,
  getTextColorStyles,
  getTypographyTokenStyle,
  HoverEffectVariant,
  HoverIntensity,
  MaterialIcon,
  RawSpacingInput,
  RenderText,
  RichText,
  SearchBar,
  TextAlignment,
  useRtl,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { cn } from '@/lib/cn'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { useDebounceInput } from '@/shared/makyo-hooks'
import { getSafely } from '@/shared/safely'
import { cleanUpText } from '@/shared/utils'

import { CompHeadline } from '../../shared/CompHeadline'
import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'
import { CompAccordionProps } from './comp'

export interface CompAccordionWrapperProps {
  infos: CoreComponentInfos
  tableData: CoreComponentTableData
  children: (props: CompAccordionProps) => React.ReactNode
}

/**
 * CompAccordionWrapperCore
 */
export const CompAccordionWrapperCore = ({
  infos: { settings, currentParentBg, containerClient, detailRowIndex, detailTableId, component },
  tableData: { dbTable: table },
  children,
}: CompAccordionWrapperProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const dir = useRtl()
  const compId = component._id

  const { checkVisibility } = useCheckVisibilityV2()
  const { simulateAction } = useBuilderActionContextV2()

  // ========================= data =========================
  const [page] = useState(0)
  const maxItems = getSafely(settings, [ComponentSettingKey.AccordionDesignItemsPerPage], 5)

  const filterSetting = settings[ComponentSettingKey.AccordionDataFilter] as ColumnConditionValue | undefined
  const sortType = settings[ComponentSettingKey.AccordionDataSortType] as string
  const sortBy = settings[ComponentSettingKey.AccordionDataSortBy] as string

  const [debouncedSearch, , , currentSearch, setCurrentSearch] = useDebounceInput('')

  const [{ rows, allRowsCount, filteredRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    pagination: { page, perPage: maxItems },
    filterSetting,
    sortSetting: { sortType, sortBy },
    search: debouncedSearch,
  })

  const titleColId = getSafely(settings, [ComponentSettingKey.AccordionDataTitle], '')
  const contentColId = getSafely(settings, [ComponentSettingKey.AccordionDataContent], '')
  const titleCol = table.columnsMap.get(titleColId)
  const contentCol = table.columnsMap.get(contentColId)

  // groupBy
  const groupBy = settings[ComponentSettingKey.AccordionDataGroupBy] as string

  // map groupBy to entries using object.groupBy
  const groupEntries: [string, IRow[]][] = useMemo(() => {
    if (!groupBy) return [['', rows]]

    const groupByCol = table.columnsMap.get(groupBy)
    if (!groupByCol) return [['', rows]]

    return Object.entries(Object.groupBy(rows, (item) => item[groupByCol._id].toString()) as Record<string, IRow[]>)
    //
  }, [groupBy, rows, table.columnsMap])

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.AccordionDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.AccordionDesignAlignmentComponent] ?? 'parent'
  const margin = settings[ComponentSettingKey.AccordionDesignComponentMargin] as RawSpacingInput

  const hoverEffect = settings[ComponentSettingKey.AccordionDesignHoverEffect]

  // const groupHeaderColor = settings[ComponentSettingKey.AccordionDesignGroupHeaderColor] || 'on-primary'

  // search box
  const searchAlign = settings[ComponentSettingKey.AccordionDesignGroupSearchAlign] ?? 'center'
  const searchBgColor = settings?.[ComponentSettingKey.AccordionDesignSearchColor] ?? 'primary-90'
  const searchCornerRadius = settings[ComponentSettingKey.AccordionDesignSearchCornerRadius] as CornerRadius
  const searchMargin = settings[ComponentSettingKey.AccordionDesignSearchMargin] as RawSpacingInput
  const searchWidth = settings[ComponentSettingKey.AccordionDesignSearchWidth]?.[0] ?? '100%'
  const searchWidthValue = Number(String(searchWidth).replace(/[^0-9.]/g, '')) || 100
  const searchWidthUnit = String(searchWidth).replace(/[0-9.]/g, '') || '%'

  const multiple = getSafely(settings, [ComponentSettingKey.AccordionBehaviorMultiExpand], false)
  // const iconLeading = getSafely(settings, [ComponentSettingKey.AccordionDesignIconLeading], false)
  const groupHeaderAlign = settings[ComponentSettingKey.AccordionDesignAlignmentGroupHeader] || 'center'
  const compAlignmentComponent = getSafely(settings, [ComponentSettingKey.AccordionDesignAlignmentComponent], 'center')

  // spacing
  // const componentPadding = settings[ComponentSettingKey.AccordionDesignComponentPadding] as SpacingInputValue
  // const panelHeaderMargin = parseSpacing(
  //   settings[ComponentSettingKey.AccordionDesignPanelHeaderMargin] as SpacingInputValue
  // )
  // const panelContentMargin = parseSpacing(
  //   settings[ComponentSettingKey.AccordionDesignPanelContentMargin] as SpacingInputValue
  // )
  const groupHeaderMargin = parseSpacing(
    settings[ComponentSettingKey.AccordionDesignGroupHeaderMargin] as SpacingInputValue
  )

  // typography
  const typoSettings = settings[ComponentSettingKey.AccordionDesignTypography] as TypographyGroupValue
  const headerTypo = typoSettings[ComponentSettingKey.AccordionDesignHeaderTypography]
  const contentTypo = typoSettings[ComponentSettingKey.AccordionDesignContentTypography]
  const groupHeaderTypo = typoSettings[ComponentSettingKey.AccordionDesignGroupHeaderTypography]

  // ========================= component title / headline =========================
  const headline = settings[ComponentSettingKey.AccordionDataCompTitle] ?? []
  const headlineMargin = settings[ComponentSettingKey.AccordionDesignTitleMargin]
  const headlineAlign = getSafely(settings, [ComponentSettingKey.AccordionDesignAlignmentTitle], 'center')
  const headlineTypo = typoSettings[ComponentSettingKey.AccordionDesignTitleTypography]
  // const hasCompTitle = (headline && !!headline[0]?.children[0]?.text) || (headline && headline[0]?.children?.length > 1)

  // color
  const compTitleColor = settings[ComponentSettingKey.AccordionDesignTitleColor] || 'adaptive'
  const compTitleColorSolid =
    (settings[ComponentSettingKey.AccordionDesignTitleColorSolid] as ColorVariant) || 'primary-40'
  const compTitleColorStartGradient =
    (settings[ComponentSettingKey.AccordionDesignTitleColorStartGradient] as ColorVariant) || 'primary-40'
  const compTitleColorEndGradient =
    (settings[ComponentSettingKey.AccordionDesignTitleColorEndGradient] as ColorVariant) || 'primary-40'
  const compTitleColorGradientDirection = settings[ComponentSettingKey.AccordionDesignTitleColorGradientDirection]

  const groupHeaderColor = settings[ComponentSettingKey.AccordionDesignGroupHeaderColor] || 'adaptive'
  const groupHeaderColorSolid =
    (settings[ComponentSettingKey.AccordionDesignGroupHeaderColorSolid] as ColorVariant) || 'primary-40'
  const groupHeaderColorStartGradient =
    (settings[ComponentSettingKey.AccordionDesignGroupHeaderColorStartGradient] as ColorVariant) || 'primary-40'
  const groupHeaderColorEndGradient =
    (settings[ComponentSettingKey.AccordionDesignGroupHeaderColorEndGradient] as ColorVariant) || 'primary-40'
  const groupHeaderColorGradientDirection =
    settings[ComponentSettingKey.AccordionDesignGroupHeaderColorGradientDirection]

  const contentColor = settings[ComponentSettingKey.AccordionDesignContentColor] || 'adaptive'
  const contentColorSolid =
    (settings[ComponentSettingKey.AccordionDesignContentColorSolid] as ColorVariant) || 'primary-40'
  const contentColorStartGradient =
    (settings[ComponentSettingKey.AccordionDesignContentColorStartGradient] as ColorVariant) || 'primary-40'
  const contentColorEndGradient =
    (settings[ComponentSettingKey.AccordionDesignContentColorEndGradient] as ColorVariant) || 'primary-40'
  const contentColorGradientDirection = settings[ComponentSettingKey.AccordionDesignContentColorGradientDirection]

  const textColor = settings[ComponentSettingKey.AccordionDesignTextColor] || 'adaptive'
  const textColorSolid = (settings[ComponentSettingKey.AccordionDesignTextColorSolid] as ColorVariant) || 'primary-40'
  const textColorStartGradient =
    (settings[ComponentSettingKey.AccordionDesignTextColorStartGradient] as ColorVariant) || 'primary-40'
  const textColorEndGradient =
    (settings[ComponentSettingKey.AccordionDesignTextColorEndGradient] as ColorVariant) || 'primary-40'
  const textColorGradientDirection = settings[ComponentSettingKey.AccordionDesignTextColorGradientDirection]

  const titleAccordionAlign = settings[ComponentSettingKey.AccordionDesignAlignmentTitleAccordion] || 'center'
  const contentAccordionAlign = settings[ComponentSettingKey.AccordionDesignAlignmentContentAccordion] || 'center'

  // add button
  const addButtonColor = settings[ComponentSettingKey.AccordionDesignAddButtonColor] ?? 'primary-20'
  const addButtonCornerRadius = settings[ComponentSettingKey.AccordionDesignAddButtonCornerRadius] as CornerRadius
  const addButtonPosition = settings[ComponentSettingKey.AccordionBehaviorAddButtonPosition] ?? 'inline'

  // ========================= behavior =========================
  const withSearch = settings[ComponentSettingKey.AccordionBehaviorUserSearch] as boolean
  const withSearchResultsInfo = settings[ComponentSettingKey.AccordionBehaviorSearchResultsInfo] as boolean

  const withAddButton = settings[ComponentSettingKey.AccordionBehaviorAddButtonShow] as boolean
  const addButtonVisibility = settings[ComponentSettingKey.AccordionBehaviorAddButtonVisibility] as
    | ConditionValue
    | undefined

  const showAddButton =
    withAddButton &&
    checkVisibility({
      visibility: addButtonVisibility,
      layerTableId: table.id,
    })

  // ========================= actions =========================
  const handleAddButtonClick = () => {
    simulateAction({
      actions: (settings[ComponentSettingKey.AccordionActionAddButtonClick] ?? []) as IActionRepeaterValue[],
      tableId: table.id,
    })
  }

  // ========================= derived =========================

  const groupHeaderStyles = {
    ...getTypographyTokenStyle(groupHeaderTypo),
    ...groupHeaderMargin,
    textAlign: groupHeaderAlign,
  }

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // if there are no rows after filtering, don't show the component
  const noDataToShow = !!filterSetting && allRowsCount === 0
  if (noDataToShow) return null

  const showSearchResultsInfo = withSearchResultsInfo && !isLoading && withSearch && debouncedSearch.trim() !== ''

  const getCompAccordionProps = (rowItems: IRow[]): CompAccordionProps => {
    return {
      dir,
      type: multiple ? 'multiple' : 'single',
      titleTypography: headerTypo,
      contentTypography: contentTypo,
      hoverEffect: hoverEffect as HoverEffectVariant,
      hoverIntensity: 'md' as HoverIntensity,
      style: {
        backgroundColor: `var(--color-${currentParentBg})`,
        color: `var(--color-on-${currentParentBg})`,
      },
      items: rowItems.map((row) => {
        return {
          value: row._id,
          trigger: row[titleColId]?.toString() ?? '',
          content: row[contentColId]?.toString() ?? '',
        }
      }),
      renderTrigger: (text) => {
        return titleCol?._Type === ColumnTypes.RichText ? (
          <RichText
            text={cleanUpText(text as string)}
            backgroundColor={currentParentBg}
            textColorType={textColor}
            textColorSolid={textColorSolid}
            textColorStartGradient={textColorStartGradient}
            textColorEndGradient={textColorEndGradient}
            textColorGradientDirection={textColorGradientDirection}
          />
        ) : (
          <RenderText
            colorType={textColor}
            colorSolid={textColorSolid}
            colorStartGradient={textColorStartGradient}
            colorEndGradient={textColorEndGradient}
            colorGradientDirection={textColorGradientDirection}
            backgroundColor={currentParentBg ?? 'primary-90'}
            value={text as string}
            typography={headerTypo}
            limit={10}
            alignment={titleAccordionAlign as ComponentAlignment}
            className='w-full'
          />
        )
      },
      renderContent: (text) => {
        return (
          <RichText
            alignment={contentAccordionAlign as ComponentAlignment}
            text={cleanUpText(text as string)}
            backgroundColor={currentParentBg}
            // Need to override the default styles of the RichText component only for specific HTML tags
            textColorType={contentColor}
            textColorSolid={contentColorSolid}
            textColorStartGradient={contentColorStartGradient}
            textColorEndGradient={contentColorEndGradient}
            textColorGradientDirection={contentColorGradientDirection}
            textAlignment={contentAccordionAlign as TextAlignment}
            style={
              {
                font: getTypographyTokenStyle(contentTypo).font,
                '--md-sys-typescale-html-paragraph': getTypographyTokenStyle(contentTypo).font,
                '--md-sys-typescale-html-ul': getTypographyTokenStyle(contentTypo).font,
                '--md-sys-typescale-html-ol': getTypographyTokenStyle(contentTypo).font,
                '--md-sys-typescale-html-blockquote': getTypographyTokenStyle(contentTypo).font,
                '--md-sys-typescale-html-code': getTypographyTokenStyle(contentTypo).font,
                '--md-sys-typescale-html-pre': getTypographyTokenStyle(contentTypo).font,
                '--md-sys-typescale-html-a': getTypographyTokenStyle(contentTypo).font,
              } as any
            }
          />
        )
      },
    }
  }

  const render = (
    <BaseComponent id={compId} width={width} alignment={alignment} margin={margin} backgroundColor={currentParentBg}>
      <CompHeadline
        containerClient={containerClient}
        detailTableId={detailTableId}
        detailRowIndex={detailRowIndex}
        headline={headline}
        compTitleAlign={headlineAlign}
        headlineMargin={headlineMargin}
        headlineTypo={headlineTypo}
        colorType={compTitleColor}
        backgroundColor={currentParentBg}
        styles={{
          ...getTextColorStyles(
            compTitleColor,
            compTitleColorSolid,
            compTitleColorStartGradient,
            compTitleColorEndGradient,
            compTitleColorGradientDirection,
            currentParentBg ?? 'primary-90'
          ),
        }}
      />

      <div className='flex items-center gap-2 mb-4'>
        {withSearch && (
          <SearchBar
            value={currentSearch}
            onChange={(e) => {
              setCurrentSearch(e.target.value)
              // setPagination(0, pagination.perPage)
            }}
            onClear={() => {
              setCurrentSearch('')
            }}
            backgroundColor={searchBgColor}
            customWidth={{
              type: searchWidthUnit === '%' ? 'percentage' : 'pixel',
              value: searchWidthValue,
            }}
            // margin={searchMargin}
            borderStyles={{
              radius: searchCornerRadius,
            }}
            align={searchAlign}
            inputClassName='py-2.5'
          />
        )}

        {showAddButton && addButtonPosition === 'inline' && (
          <button
            className={cn(
              'p-2 min-h-10 min-w-10 flex items-center justify-center cursor-pointer outline-none ring-0 focus:ring-0 focus:outline-none',
              addButtonColor === currentParentBg && `border border-on-${addButtonColor}/50`,
              getShapeClass(addButtonCornerRadius),
              `bg-${addButtonColor} text-on-${addButtonColor} hover:bg-${addButtonColor}/90`
            )}
            onClick={handleAddButtonClick}
          >
            <MaterialIcon icon='add' size={20} className={`text-on-${addButtonColor}`} />
          </button>
        )}
      </div>

      {showSearchResultsInfo && (
        <div
          className={cn('px-1 mb-4 text-sm', `text-on-${currentParentBg}/80`)}
        >{`Showing ${thousandFmtNoDecimal.format(filteredRowsCount)} results of ${thousandFmtNoDecimal.format(allRowsCount)}`}</div>
      )}

      {/*// TODO: skeleton */}
      {isLoading ? (
        <div>Loading...</div>
      ) : groupBy ? (
        <div className='flex flex-col gap-4'>
          {groupEntries.map(([k, groupRows]) => {
            return (
              <div key={k} className='flex flex-col gap-4'>
                <div style={groupHeaderStyles}>
                  <RenderText
                    colorType={groupHeaderColor}
                    colorSolid={groupHeaderColorSolid}
                    colorStartGradient={groupHeaderColorStartGradient}
                    colorEndGradient={groupHeaderColorEndGradient}
                    colorGradientDirection={groupHeaderColorGradientDirection}
                    backgroundColor={currentParentBg ?? 'primary-90'}
                    value={k}
                    typography={groupHeaderTypo}
                    alignment={groupHeaderAlign as ComponentAlignment}
                    limit={10}
                    className='w-full'
                  />
                </div>
                {children(getCompAccordionProps(groupRows))}
              </div>
            )
          })}
        </div>
      ) : (
        children(getCompAccordionProps(rows))
      )}

      {showAddButton && addButtonPosition === 'bottom' && (
        <div className='mt-4'>
          <Button
            colorVariant={addButtonColor}
            fullWidth
            onClick={handleAddButtonClick}
            icon={<MaterialIcon icon='add' size={20} />}
          >
            Add Item
          </Button>
        </div>
      )}
    </BaseComponent>
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {render}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return render
}
